import { AdjustPaymentRequest } from '../request-models/adjust-payment-request.model';
import { AllPaymentsFilters } from '../request-models/all-payments-filters.model';
import { ApprovePaymentRequest } from '../request-models/approve-payment-request.model';
import { DeclinePaymentRequest } from '../request-models/decline-payment-request.model';
import { EarlyReleaseRequest } from '../request-models/early-release-request.model';
import { PendingApprovalFilters } from '../request-models/pending-approval-filters.model';
import { omitUndefined } from './wire-value-utils';

/** Query values for `opts.query`; blank values are left `undefined`, so they are not sent. */
type PaymentQuery = Record<string, string | number | undefined>;

/** POST /admin/payments/pending-approval/:id/adjust body: exactly what the hub reads. */
export function toAdjustPaymentRequest(fields: AdjustPaymentRequest): AdjustPaymentRequest {
  return { billableAcres: fields.billableAcres, pricePerAcre: fields.pricePerAcre, reason: fields.reason };
}

/** POST /admin/payments/pending-approval/:id/approve body: notes and the optional inline adjustment, when set. */
export function toApprovePaymentRequest(fields: ApprovePaymentRequest): ApprovePaymentRequest {
  return omitUndefined<ApprovePaymentRequest>({
    approvalNotes: fields.approvalNotes,
    adjustment: fields.adjustment ? toAdjustPaymentRequest(fields.adjustment) : undefined,
  });
}

/** POST /admin/payments/pending-approval/:id/decline body. */
export function toDeclinePaymentRequest(fields: DeclinePaymentRequest): DeclinePaymentRequest {
  return { reason: fields.reason };
}

/** POST /admin/payments/payouts/:id/release body: an audited window bypass always carries its reason. */
export function toEarlyReleaseRequest(reason: string): EarlyReleaseRequest {
  return { ignoreWindow: true, reason };
}

/** Query for GET /admin/payments/pending-approval (paged with `limit` / `offset`). */
export function toPendingApprovalQuery(filters: PendingApprovalFilters): PaymentQuery {
  return {
    farmerOrgId: filters.farmerOrgId || undefined,
    pilotOrgId: filters.pilotOrgId || undefined,
    createdAtFrom: filters.createdAtFrom || undefined,
    createdAtTo: filters.createdAtTo || undefined,
    minTotalCharge: filters.minTotalCharge ?? undefined,
    maxTotalCharge: filters.maxTotalCharge ?? undefined,
    limit: filters.limit ?? undefined,
    offset: filters.offset ?? undefined,
  };
}

/** Query for GET /admin/payments: statuses go as one comma-separated `status`; none means every status. */
export function toAllPaymentsQuery(filters: AllPaymentsFilters): PaymentQuery {
  const statuses = filters.statuses?.length ? filters.statuses.join(',') : undefined;
  return { status: statuses, ...toPendingApprovalQuery(filters) };
}
